import React, { useEffect, useState, useRef } from "react";
import { Modal, Form, Input, Select, Row, Col, Button, DatePicker, Alert } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { formatLabel, selectFieldRules } from "../../utils";
import dayjs from "dayjs";
import { useUpdateWarrantyMutation } from "../../store/api/serviceApi";
import { useGetEntityFilesQuery } from "../../store/api/fileApi";
import { useToast } from "../../hooks/useToast";
import S3FileUpload, { S3FileUploadRef } from "./S3FileUpload";
import UploadedFilesList from "./UploadedFilesList";
import type { ServiceResponse } from "@OrianaTypes";

const WARRANTY_ENTITY_TYPE = "warranty_certificate";

interface WarrantyCertificateFormModalProps {
  visible: boolean;
  onClose: () => void;
  poId: string;
  editData?: ServiceResponse | null;
}

const WarrantyCertificateFormModal: React.FC<WarrantyCertificateFormModalProps> = ({
  visible,
  onClose,
  poId,
  editData = null,
}) => {
  const [form] = Form.useForm();
  const toast = useToast();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const fileUploadRef = useRef<S3FileUploadRef>(null);

  const [updateWarranty, { isLoading: isUpdating }] = useUpdateWarrantyMutation();

  const serviceIdForFiles = editData?.serviceId != null ? String(editData.serviceId) : "";
  const { data: warrantyFilesRes } = useGetEntityFilesQuery(
    { entityType: WARRANTY_ENTITY_TYPE, entityId: serviceIdForFiles },
    { skip: !serviceIdForFiles || !visible }
  );
  const warrantyFiles = warrantyFilesRes?.data ?? [];

  const warrantyPeriod = (editData?.warranty || "").split("_").join(" ").toUpperCase();
  // Status options - using lowercase to match backend
  const statusOptions = [
    { value: "done", label: "Done" },
    { value: "pending", label: "Pending" },
    { value: "hold", label: "Hold" },
    { value: "cancelled", label: "Cancelled" },
  ];

  useEffect(() => {
    if (visible && editData) {
      form.setFieldsValue({
        warrantyCertificateNo: editData.warrantyCertificateNo || "",
        warrantyIssueDate: editData.warrantyIssueDate
          ? dayjs(editData.warrantyIssueDate)
          : null,
        warrantyStartDate: editData.warrantyStartDate
          ? dayjs(editData.warrantyStartDate)
          : null,
        warrantyEndDate: editData.warrantyEndDate
          ? dayjs(editData.warrantyEndDate)
          : null,
        warrantyStatus: editData.warrantyStatus || "",
      });
    } else if (visible) {
      form.resetFields();
    }
  }, [visible, editData, form]);

  const handleSubmit = async () => {
    if (!editData) {
      toast.error("Please select a service to update");
      return;
    }

    try {
      const values = await form.validateFields();

      await updateWarranty({
        id: editData.serviceId,
        data: {
          warrantyCertificateNo: values.warrantyCertificateNo || undefined,
          warrantyIssueDate: values.warrantyIssueDate?.format("YYYY-MM-DD") || undefined,
          warrantyStartDate: values.warrantyStartDate?.format("YYYY-MM-DD") || undefined,
          warrantyEndDate: values.warrantyEndDate?.format("YYYY-MM-DD") || undefined,
          warrantyStatus: values.warrantyStatus || undefined,
        },
        poId,
      }).unwrap();
      toast.success("Warranty certificate updated successfully");
      handleCancel();
    } catch (error) {
      console.error("Submission failed:", error);
      toast.error("Failed to update warranty certificate");
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    onClose();
  };

  if (!editData) {
    return (
      <Modal
        title="Update Warranty Certificate"
        open={visible}
        onCancel={handleCancel}
        width={800}
        destroyOnClose
        footer={[
          <Button key="close" onClick={handleCancel}>
            Close
          </Button>,
        ]}
      >
        <Alert
          type="info"
          message="No Service Selected"
          description="Please select a service from the table to update warranty certificate details."
          showIcon
        />
      </Modal>
    );
  }

  return (
    <Modal
      title={`Update Warranty Certificate - Service #${editData.serviceId}`}
      open={visible}
      onCancel={handleCancel}
      width={800}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isUpdating}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={isUpdating}
          style={{ backgroundColor: "#4b6cb7" }}
        >
          Update
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        style={{ marginTop: "1rem" }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label="Service ID">
              <Input value={`#${editData.serviceId}`} disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Serial Number">
              <Input value={editData.serialNumber} disabled />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="warrantyCertificateNo"
              label="Certificate No"
            >
              <Input placeholder="Warranty certificate number" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="warrantyIssueDate" label="Issue Date">
              <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item name="warrantyStartDate" label={`Warranty Start Date (Warranty Period: ${warrantyPeriod || "N/A"})`}>
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                onChange={(date) => {
                  if (date && warrantyPeriod) {
                    const years = Number(warrantyPeriod.split(" ")[0]);
                    if (!Number.isNaN(years)) {
                      const endDate = date.add(years, "year");
                      setTimeout(() => {
                        form.setFieldValue("warrantyEndDate", endDate);
                      }, 0);
                    }
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="warrantyEndDate" label="Warranty End Date">
              <DatePicker disabled={!!warrantyPeriod} style={{ width: "100%" }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="warrantyStatus"
              label="Warranty Status"
              rules={selectFieldRules}
            >
              <Select placeholder="Select status" options={statusOptions} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              label="Upload Document (Warranty certificate)"
              tooltip="Upload warranty certificate documents. Supported formats: Images, PDF, Word, Excel"
            >
              <S3FileUpload
                ref={fileUploadRef}
                fileList={fileList}
                onChange={setFileList}
                minFiles={0}
                maxFiles={10}
                maxSizeMB={10}
                label=""
                buttonLabel="Click to Upload"
                helperText="Supported: Images (JPG, PNG, GIF, SVG), PDF, Word, Excel."
                poId={poId}
                entityType={WARRANTY_ENTITY_TYPE}
                entityId={serviceIdForFiles || undefined}
              />
              <UploadedFilesList
                files={warrantyFiles}
                title="Warranty Certificate Documents"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default WarrantyCertificateFormModal;
